import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Box,
  Typography,
} from '@mui/material';
import { ApiKeyField } from './ApiKeyField';
import { NovaPoshtaCitySelect, NovaPoshtaWarehouseSelect } from '../../../common/novaposhta';
import { novaPoshtaReference } from '../../../../services/novaPoshtaClient';
import type { CitySearchResult, WarehouseSearchResult } from '../../../../services/novaPoshtaClient';
import type { ShippingSender, CreateShippingSenderRequest, UpdateShippingSenderRequest } from '../../../../types/shippingSender';

interface ShippingSenderDialogProps {
  open: boolean;
  sender: ShippingSender | null;
  onClose: () => void;
  onCreate: (request: CreateShippingSenderRequest) => Promise<string | null>;
  onUpdate: (senderId: number, request: UpdateShippingSenderRequest) => Promise<string | null>;
  submitting: boolean;
  error: string | null;
  /** Fetches the already-configured plaintext key, just for driving the sender-address
   * Autocompletes in edit mode — never persisted, same transient trust model as everywhere
   * else this key is handled (see doc/shipping/novaposhta-integration.md#security). */
  fetchApiKeyForSearch: (senderId: number) => Promise<string>;
}

export function ShippingSenderDialog({
  open,
  sender,
  onClose,
  onCreate,
  onUpdate,
  submitting,
  error,
  fetchApiKeyForSearch,
}: ShippingSenderDialogProps) {
  const [label, setLabel] = useState('');
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [apiKeyForSearch, setApiKeyForSearch] = useState<string | null>(null);
  const [senderCity, setSenderCity] = useState<CitySearchResult | null>(null);
  const [senderWarehouse, setSenderWarehouse] = useState<WarehouseSearchResult | null>(null);

  useEffect(() => {
    if (!open) return;

    setLabel(sender?.label ?? '');
    setApiKeyInput('');
    setApiKeyForSearch(null);
    // Shown as the raw Ref until resolveSavedAddress (below) fetches the real name from
    // Nova Poshta — avoids a blank field while that lookup is in flight.
    setSenderCity(
      sender?.defaultSenderCityRef
        ? { ref: sender.defaultSenderCityRef, present: sender.defaultSenderCityRef }
        : null
    );
    setSenderWarehouse(
      sender?.defaultSenderWarehouseRef
        ? { ref: sender.defaultSenderWarehouseRef, number: '', description: sender.defaultSenderWarehouseRef, category: '' }
        : null
    );

    if (sender?.apiKeyConfigured) {
      fetchApiKeyForSearch(sender.id).then(setApiKeyForSearch).catch(() => setApiKeyForSearch(null));
    }
  }, [open, sender, fetchApiKeyForSearch]);

  const searchApiKey = apiKeyInput.trim() || apiKeyForSearch;

  // Resolves the saved city/warehouse Refs to their real Nova Poshta names via live
  // Address/getCities and Address/getWarehouses calls, once a key becomes available to
  // search with. Guards against overwriting a selection the user already changed in the
  // meantime by checking the ref is still the one we resolved for.
  useEffect(() => {
    if (!open || !sender || !searchApiKey) return;

    const cityRef = sender.defaultSenderCityRef;
    if (cityRef) {
      novaPoshtaReference.getCityByRef(searchApiKey, cityRef).then((city) => {
        if (!city) return;
        setSenderCity((prev) => (prev?.ref === cityRef ? city : prev));
      }).catch((err: unknown) => {
        // Non-fatal — the raw Ref set above stays shown instead of the resolved name.
        console.warn('Не вдалося визначити назву збереженого міста відправлення', err);
      });
    }

    const warehouseRef = sender.defaultSenderWarehouseRef;
    if (warehouseRef) {
      novaPoshtaReference.getWarehouseByRef(searchApiKey, warehouseRef).then((warehouse) => {
        if (!warehouse) return;
        setSenderWarehouse((prev) => (prev?.ref === warehouseRef ? warehouse : prev));
      }).catch((err: unknown) => {
        console.warn('Не вдалося визначити назву збереженого складу відправлення', err);
      });
    }
  }, [open, sender, searchApiKey]);

  // City and warehouse must always be saved as a matching pair — the backend treats a
  // missing ref as "keep the previous value" (see ShippingSenderService#updateSender), so
  // picking a new city without also picking a warehouse in it would otherwise leave the
  // profile with its *old* warehouse paired against the *new* city.
  const handleSenderCityChange = (city: CitySearchResult | null) => {
    setSenderCity(city);
    setSenderWarehouse(null);
  };

  const addressNeedsWarehouse = Boolean(senderCity) && !senderWarehouse;

  const handleSubmit = async () => {
    const result = sender
      ? await onUpdate(sender.id, {
          label,
          apiKey: apiKeyInput || undefined,
          defaultSenderCityRef: senderCity?.ref,
          defaultSenderWarehouseRef: senderWarehouse?.ref,
        })
      : await onCreate({
          label,
          apiKey: apiKeyInput,
          defaultSenderCityRef: senderCity?.ref,
          defaultSenderWarehouseRef: senderWarehouse?.ref,
        });

    if (!result) {
      onClose();
    }
  };

  const canSubmit = label.trim() && (sender ? true : apiKeyInput.trim()) && !addressNeedsWarehouse;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{sender ? 'Редагувати профіль відправника' : 'Новий профіль відправника'}</DialogTitle>
      <DialogContent>
        <TextField
          label="Назва профілю"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          disabled={submitting}
          fullWidth
          size="small"
          margin="normal"
          helperText="Як цей профіль буде підписаний у виборі відправника, напр. «ФОП Іванов»"
        />

        <ApiKeyField
          value={apiKeyInput}
          configured={sender?.apiKeyConfigured ?? false}
          disabled={submitting}
          onChange={setApiKeyInput}
        />

        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
          Склад відправлення
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Місто й відділення/поштомат, звідки цей профіль відправляє посилки.
          {!searchApiKey && ' Введіть API-ключ вище, щоб увімкнути пошук.'}
        </Typography>
        <Box sx={{ mb: 1.5 }}>
          <NovaPoshtaCitySelect
            apiKey={searchApiKey}
            value={senderCity}
            onChange={handleSenderCityChange}
            label="Місто відправлення"
            disabled={submitting}
          />
        </Box>
        <NovaPoshtaWarehouseSelect
          apiKey={searchApiKey}
          cityRef={senderCity?.ref ?? null}
          value={senderWarehouse}
          onChange={setSenderWarehouse}
          label="Відділення/поштомат відправлення"
          disabled={submitting}
        />
        {addressNeedsWarehouse && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
            Оберіть відділення/поштомат для нового міста
          </Typography>
        )}

        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Скасувати
        </Button>
        <Button
          variant="contained"
          startIcon={submitting ? <CircularProgress size={16} /> : undefined}
          onClick={handleSubmit}
          disabled={submitting || !canSubmit}
        >
          Зберегти
        </Button>
      </DialogActions>
    </Dialog>
  );
}
